import React, { useState, useCallback, useRef, FunctionComponent } from 'react';
import { Presentation } from './presentation';
import { useModal } from '../../../hooks/useModal';
import { useLoginRegisterModals } from '../../../hooks/useLoginRegisterModals';
import { createProspectiveUser } from '../../../services/prospectiveUsers';
import { getCsrfToken } from '../../../libs/csrfToken';
import { postConnectRecruitment } from '../../../services/personConnectRecruitments';
import { DataCategory, DataValueRecruitmentShowContent, DataValueRecruitmentShowContentBrand, Occupation, PreferredOccupation, Prefecture, OccupationDetail, GraduationEntry, DataValueRecruitmentRegisterModal } from '../../../types/recruitmentShow';

type Props = {
  recruitmentId?: number;
  companyId: number;
  adminBrandId?: number;
  isStopped?: boolean;
  isUser?: boolean;
  user?: 'company' | 'person' | 'without_login';
  noimage: string;
  occupationName?: string;
  occupationUrl?: string;
  industryUrl?: string;
  isCompany?: boolean;
  dataCategory?: string | DataCategory;
  dataValue?: DataValueRecruitmentShowContent | DataValueRecruitmentShowContentBrand;
  isDisplayed?: boolean;
  isShow?: boolean;
  hoverIcon?: string;
  cancelIcon?: string;
  brandId?: number;
  instagramIcon?: string;
  isPc?: boolean;
  isSp?: boolean;
  isBrandPage?: boolean;
  isCompanyPage?: boolean;
  isStoppedFv?: boolean;
  lineIcon: string;
  isPreview?: boolean;
  // RecruitmentRegisterModal用
  occupations?: Occupation[];
  preferredOccupations?: PreferredOccupation[];
  occupationDetails?: OccupationDetail[];
  prefectures?: Prefecture[];
  graduationArray?: GraduationEntry[];
  graduationAnnotaion?: number;
  dataCategoryRegisterModal?: string;
  dataValueRegisterModal?: DataValueRecruitmentRegisterModal;
  // Connect Modal用
  isScouted?: boolean;
  isStudentAndMidCareer?: boolean;
  hasRequiredProfiles?: boolean;
  needProfileUpdate?: boolean;
  requiredProfileNames?: string[];
  relatedRecruitments?: any[];
  companyName?: string;
  userId?: number;
  // Login Modal用
  isLoginError?: boolean;
  modalFlashMessage?: string;
};

export const Component: FunctionComponent<Props> = (props) => {
  const { recruitmentId, companyId, isUser, isCompany, userId } = props;

  // State管理
  const [flash, setFlash] = useState(false);
  const [flashType, setFlashType] = useState('');
  const [flashMessage, setFlashMessage] = useState('');
  const [isLogin, setIsLogin] = useState(false);
  const [connectMessageBoxId, setConnectMessageBoxId] = useState<number | null>(null);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);

  // エントリーモーダル関連
  const { isOpen: isConnectModalOpen, close: handleConnectModalClose, open: handleConnectModalOpen } = useModal(false, { lockBody: false });
  const { isOpen: isConnectCompleteModalOpen, close: handleConnectCompleteModalClose, open: handleConnectCompleteModalOpen } = useModal(false, { lockBody: false });
  const [interest, setInterest] = useState<'want_to_work' | 'want_to_hear' | 'interesting'>('want_to_work');

  // ログインモーダル関連
  const {
    isLoginModalOpen,
    isRegisterModalOpen,
    handleToggleLoginModal,
    handleToggleRegisterModal,
    handleChangeModal,
    redirectUrl,
  } = useLoginRegisterModals(false);

  // 通知メール受け取り用
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // ハンドラー関数
  const handleRegisterModalOpen = useCallback(() => {
    handleToggleRegisterModal();
    if (isRegisterModalOpen) {
      document.body.removeAttribute('style');
    } else {
      document.body.style.overflow = 'hidden';
    }
  }, [isRegisterModalOpen, handleToggleRegisterModal]);

  const handleSwitchLoginModalOpen = useCallback(() => {
    handleToggleRegisterModal();
    handleToggleLoginModal();
  }, [handleToggleRegisterModal, handleToggleLoginModal]);

  const handleFlash = useCallback((flash: boolean, flashType: string, flashMessage: string) => {
    setFlash(flash);
    setFlashType(flashType);
    setFlashMessage(flashMessage);
  }, []);

  const handleIsLogin = useCallback(() => {
    setIsLogin(true);
  }, []);

  const handleClickModalBg = useCallback(() => {
    handleConnectModalClose();
    handleConnectCompleteModalClose();
  }, [handleConnectModalClose, handleConnectCompleteModalClose]);

  const handleSubmitConnect = useCallback(async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (isSubmittingRef.current || !recruitmentId) return;
    isSubmittingRef.current = true;
    setIsSubmitting(true);

    try {
      const body = {
        how_interested: interest,
        recruitment_id: recruitmentId
      };
      const response = await postConnectRecruitment(body, getCsrfToken());
      if (response.errormessage) {
        alert(`エラーが発生しました：${response.errormessage}`);
        return;
      } else {
        setConnectMessageBoxId(response.messageBoxId ?? null);
        // dataLayer.push
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'entry_recruitment',
          event_recruitment_id: recruitmentId,
          event_company_id: companyId,
        });
        handleConnectModalClose();
        handleConnectCompleteModalOpen();
        setIsConnected(true);
      }
    } catch (error) {
      console.error(error);
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [interest, recruitmentId, companyId, handleConnectModalClose, handleConnectCompleteModalOpen]);

  const handleChangeInterest = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.currentTarget.value as 'want_to_work' | 'want_to_hear' | 'interesting';
      setInterest(value);
    },
    []
  );

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailSubmit = async () => {
    if (!email) {
      alert('メールアドレスを入力してください');
      return;
    }

    if (!props.adminBrandId && !recruitmentId) {
      alert('通知対象の求人またはブランドが見つかりませんでした。');
      return;
    }

    if (!validateEmail(email)) {
      alert('正しいメールアドレスの形式で入力してください');
      return;
    }

    const csrfToken = getCsrfToken();
    if (!csrfToken) {
      alert('CSRFトークンが取得できませんでした。ページをリロードしてください。');
      return;
    }

    setIsLoading(true);
    try {
      await createProspectiveUser(
        {
          email: email,
          admin_brand_id: props.adminBrandId,
          recruitment_id: recruitmentId
        },
        csrfToken
      );
      setIsSubmitted(true);
      handleRegisterModalOpen?.();
      $('html, body').css('overflow', 'hidden');
    } catch (error) {
      console.error('Error creating prospective user:', error);
      alert('エラーが発生しました。もう一度お試しください。');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Presentation
      {...props}
      redirectUrl={redirectUrl}
      isLoginModalOpen={isLoginModalOpen}
      isRegisterModalOpen={isRegisterModalOpen}
      isConnectModalOpen={isConnectModalOpen}
      isConnectCompleteModalOpen={isConnectCompleteModalOpen}
      interest={interest}
      isSubmitting={isSubmitting}
      handleRegisterModalOpen={handleRegisterModalOpen}
      handleSwitchLoginModalOpen={handleSwitchLoginModalOpen}
      handleConnectModalOpen={handleConnectModalOpen}
      handleIsLogin={handleIsLogin}
      handleFlash={handleFlash}
      flash={flash}
      flashType={flashType}
      flashMessage={flashMessage}
      handleToggleLoginModal={handleToggleLoginModal}
      handleChangeModal={handleChangeModal}
      handleClickModalBg={handleClickModalBg}
      handleSubmitConnect={handleSubmitConnect}
      handleChangeInterest={handleChangeInterest}
      email={email}
      setEmail={setEmail}
      isLoading={isLoading}
      isSubmitted={isSubmitted}
      handleEmailSubmit={handleEmailSubmit}
    />
  );
};
